import {
  ColDef,
  GridOptions,
  themeAlpine,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import capitalize from "lodash.capitalize";
import { ForwardedRef, forwardRef, useMemo } from "react";
import { useSelector } from "react-redux";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import {
  currencyFormatter,
  noDecimalNumberFormatter,
} from "@intelligems/ig-graphs/data-formatters";
import {
  AnalyticsAudienceType,
  AnalyzeTinybirdResourceResponseBodyType,
  CountryEnum,
  ExperienceFe,
  GenericExperienceFe,
  GraphConfig,
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  VariationFe,
} from "@intelligems/ig-types";
import { styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useAnalyticsAudienceFetching } from "@src/api/analytics/hooks/use-analytics-audience-fetching";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import {
  GroupCellRenderer,
  HeaderComponent,
  MetricCellRenderer,
  TableGenericCellRenderer,
  TableRolloutButtonRenderer,
} from "@src/features/analytics/tables/analyticsAudienceTable/audienceTableRenderers";
import {
  getStandardMetricByAnalyticsMetricId,
  normalizeValues,
  sortAndFlattenData,
} from "@src/features/analytics/tables/analyticsAudienceTable/utils";
import { CompareInnerRenderer } from "@src/features/analytics/tables/CompareInnerRenderer";
import {
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import { useDispatch } from "@src/store";
import { getExperienceAnalyticsPageName } from "@src/store/slices/dashboardFilters";
import { getPrimaryMetricId } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { theme } from "@src/theme";
import { getControlVariationId } from "@src/utils/controlVariation";
import { sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  pinnedColumnBorder: `1px solid ${IgGrayPrimitives[300]}`,
  rowHoverColor: theme.igBackgroundColors.interactive.neutral.hover,
});
const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:last-child:after`]: {
    backgroundColor: "transparent",
  },
  "& ::-webkit-scrollbar": {
    width: 0 /* Remove scrollbar space */,
    background: "transparent" /* Optional: just make scrollbar invisible */,
  },
  [`& .ag-root-wrapper`]: {
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
    border: "none",
  },
  [`& .ag-header`]: {
    borderBottom: `1px solid ${theme.igPrimitives.gray[300]}`,
  },
  [`& .ag-header-cell`]: {
    paddingLeft: "14px !important",
    paddingRight: "0px !important",
  },
  [`& .ag-row:last-child`]: {
    borderBottom: "solid 0px !important",
  },
  [`& .ag-row`]: {
    borderBottom: `1px solid ${theme.igPrimitives.gray[300]}`,
  },
  [`& .ag-header-cell-label`]: {
    fontWeight: 400,
    fontSize: "14px",
    borderRight: "none",
    fontFamily: `${theme.typography.fontFamily}`,
    padding: "0 !important",
  },
  [`& .ag-header-cell-comp-wrapper`]: {
    padding: "0 15px 0 0 !important ",
    justifyContent: "flex-end",
  },
  [`& .ag-cell-wrap-text`]: {
    wordBreak: "break-word",
  },
  [`&.ag-header-cell`]: {
    backgroundColor: "blue !important",
    borderRight: "none !important",
  },
  [`& .ag-header-cell:after`]: {
    display: "none",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.igPrimitives.gray["A300"]} !important`,
    fontWeight: 600,
    fontSize: "14px",
  },
  [`& .ag-pinned-left-header`]: {
    borderRight: "none !important",
    paddingLeft: "15px",
  },
  [`& .ag-cell-norm`]: {
    [`&.ag-cell`]: {
      fontSize: "16px !important",
      color: theme.palette.text.secondary,
    },
  },
  [`& .ag-cell-end`]: {
    [`&.ag-cell`]: {
      fontSize: "16px !important",
      color: theme.palette.text.secondary,
    },
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-popup-child:not(.ag-tooltip-custom)`]: {
    backgroundColor: "white",
    borderRadius: "10px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "90px !important",
  },
  [`& .ag-aria-description-container`]: {
    position: "absolute !important",
    top: "-9999px !important",
    left: "-9999px !important",
    height: "1px !important",
    width: "1px !important",
    overflow: "hidden !important",
  },
}));

const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 56;
const MAX_VISIBLE_ROWS = 12;
const GRID_VERTICAL_BUFFER = 16;

const tabValueMap = {
  0: "all",
  1: "audience",
  2: "audience",
  3: "audience",
  4: "audience",
  5: "audience",
  6: "audience",
  7: "audience",
};

interface AnalyticsSegmentTableProps {
  tabValue: number;
  experience: ExperienceFe | GenericExperienceFe;
  variations: VariationFe[];
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  segmentDataQuery: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  testDuration: number;
  metricFilterIndex?: number;
  audienceSegmentSelected: AnalyticsAudienceType | undefined;
  excludeColumns?: string[];
}

export const AnalyticsAudienceTable = forwardRef(
  (
    {
      tabValue,
      variations,
      experience,
      dashboardData,
      segmentDataQuery,
      graphConfigs,
      testDuration,
      metricFilterIndex,
      audienceSegmentSelected,
      excludeColumns,
    }: AnalyticsSegmentTableProps,
    ref: ForwardedRef<AgGridReact>
  ) => {
    const org = useGetOrg();
    const dispatch = useDispatch();
    const controlVariationId = getControlVariationId(variations);
    const graphValueFormatter = useGraphValueFormatter();
    const pageName = useSelector(getExperienceAnalyticsPageName);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const shouldShowProfit = useMemo(() => {
      return shouldShowProfitData(dashboardData[0]?.data?.metrics || []);
    }, [dashboardData]);
    const currValue = tabValueMap[tabValue];
    const isAudienceAnalyticsFetching = useAnalyticsAudienceFetching();
    const isOverviewFetching = useAnalyticsFetching();

    const selectedData = useMemo(() => {
      return tabValue === 0 ? dashboardData : segmentDataQuery;
    }, [tabValue, segmentDataQuery, dashboardData]);

    const primaryMetricId = useSelector(getPrimaryMetricId);
    const hasPersonalizationAccess = useMemo(() => {
      return checkBooleanScope(
        "personalizations",
        org?.data?.org.configurations
      );
    }, [org?.data?.org?.configurations]);
    const rows = useMemo(() => {
      if (!selectedData[0].data?.metrics || !variations?.length) return null;

      const normalizedData = selectedData[0].data.metrics.map((group: any) => ({
        ...normalizeValues(group),
        order:
          variations.find(
            // eslint-disable-next-line max-nested-callbacks
            (variation) => variation.id === group.variation_id
          )?.order ?? 0,
      }));

      // Group by category
      const groups: Record<string, any[]> = {};
      normalizedData.forEach((item) => {
        const key = item[currValue];
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      // Sort variations
      const sortedGroups = { ...groups };
      Object.keys(sortedGroups).forEach((key) => {
        if (Array.isArray(sortedGroups[key])) {
          sortedGroups[key] = sortedGroups[key].sort(sortByOrder);
        }
      });

      // Sort grouped rows by analytics sorting order
      const sortedData = sortAndFlattenData(
        sortedGroups,
        selectedData[0]?.data?.audienceOrder || []
      );

      return sortedData.map((metric, index) => ({
        ...metric,
        id: index,
      }));
    }, [selectedData, tabValue, variations, controlVariationId, currValue]);

    const rowCount = rows?.length ?? 0;
    const isGrouped = experience?.category === "experiment" && tabValue !== 0;
    const groupedRowCount =
      isGrouped && rows ? new Set(rows.map((row) => row[currValue])).size : 0;
    const totalRowCount = rowCount + groupedRowCount;
    const gridHeight = Math.min(
      HEADER_HEIGHT + totalRowCount * ROW_HEIGHT + GRID_VERTICAL_BUFFER,
      HEADER_HEIGHT + MAX_VISIBLE_ROWS * ROW_HEIGHT
    );

    const defaultColDef: ColDef = {
      flex: experience?.category === "experiment" ? 1 : 0.5,
      minWidth: 100,
      resizable: true,
      suppressSizeToFit: true,
      cellStyle: {
        fontSize: "14px",
        alignContent: "center",
        alignItems: "right",
      },
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    };

    const groupRowRendererParams = useMemo(() => {
      return {
        innerRenderer: CompareInnerRenderer,
        suppressCount: true,
        tabValue,
      };
    }, [tabValue]);

    const columnDefs: ColDef[] = useMemo(() => {
      const baseColumns: ColDef[] = [
        {
          headerName: "Audience",
          rowGroup: experience?.category === "experiment",
          hide: experience?.category === "experiment",
          field: currValue,
          suppressFillHandle: true,
          valueGetter: (params) => params.data[currValue] || "Other",
          valueFormatter: (params) => {
            if (tabValue === 5) {
              if (params.value !== null && params.value !== "Other") {
                return `${CountryEnum[params.value]} (${params.value})`;
              }
              return params.value;
            } else if (tabValue === 7) {
              return capitalize(params?.value);
            } else {
              if (params?.value === "Youtube") {
                return "YouTube";
              } else return params?.value;
            }
          },
        },
        {
          headerName: "Group",
          headerClass: "header-group",
          field: "variation_id",
          flex: 0.7,
          hide: experience?.category === "personalization",
          minWidth: 250,
          pinned: true,
          cellStyle: {
            className: "ag-cell-group",
            justifyContent: "flex-start",
            borderRight: `1px solid ${IgGrayPrimitives[300]}`,
          },
          cellRendererParams: {
            variations,
            tabValue,
            disabled: false,
            disabledReason: "",
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return (
              variations.find((variation) => variation.id === params.value)
                ?.name || ""
            );
          },
          cellRenderer: GroupCellRenderer,
        },
      ];

      const filteredGraphConfigs = graphConfigs.filter((config) => {
        return (
          pageName === "postTestMetrics" ||
          !shouldShowProfitPlaceholder(shouldShowProfit, config)
        );
      });

      const postTestMetricsColumns: ColDef[] =
        pageName === "postTestMetrics"
          ? [
              {
                headerName: "In-Test Customers",
                field: "n_customers",
                flex: 0.6,
                minWidth: 120,
                headerComponent: HeaderComponent,
                headerComponentParams: {
                  headerLabel: "In-Test Customers",
                  index: -1,
                  pageName,
                  experienceCategory: experience?.category,
                  isPrimary: false,
                },
                valueFormatter: (params: ValueFormatterParams) => {
                  if (params.value?.value !== undefined) {
                    return noDecimalNumberFormatter(
                      params.value?.value,
                      false,
                      storeCurrencyConfig!.value
                    );
                  } else return "";
                },
                cellRenderer: TableGenericCellRenderer,
              },
              {
                headerName: "Post-Test Profit",
                field: "post_gross_profit",
                flex: 0.6,
                minWidth: 120,
                headerComponent: HeaderComponent,
                headerComponentParams: {
                  headerLabel: "Post-Test Profit",
                  index: -1,
                  pageName,
                  experienceCategory: experience?.category,
                  isPrimary: false,
                },
                valueFormatter: (params: ValueFormatterParams) => {
                  if (params.value?.value !== undefined) {
                    return currencyFormatter(
                      params.value?.value,
                      false,
                      storeCurrencyConfig!.value
                    );
                  } else return "";
                },
                cellRenderer: TableGenericCellRenderer,
              },
              {
                headerName: "Post-Test Revenue",
                field: "post_net_revenue",
                flex: 0.6,
                minWidth: 120,
                headerComponent: HeaderComponent,
                headerComponentParams: {
                  headerLabel: "Post-Test Revenue",
                  index: -1,
                  pageName,
                  experienceCategory: experience?.category,
                  isPrimary: false,
                },
                valueFormatter: (params: ValueFormatterParams) => {
                  if (params.value?.value !== undefined) {
                    return currencyFormatter(
                      params.value?.value,
                      false,
                      storeCurrencyConfig!.value
                    );
                  } else return "";
                },
                cellRenderer: TableGenericCellRenderer,
              },
            ]
          : [];

      const colDef: ColDef[] = [
        {
          headerName: "Visitors",
          field: "n_visitors",
          flex: 0.6,
          headerComponent: HeaderComponent,
          headerComponentParams: {
            headerLabel:
              pageName === "postTestMetrics" ? "In-Test Visitors" : "Visitors",
            index: -1,
            pageName,
            experienceCategory: experience?.category,
            isPrimary: false,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return noDecimalNumberFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        ...postTestMetricsColumns,
        {
          headerName:
            pageName === "subscriptions" ? "Subscription Orders" : "Orders",
          flex: 0.4,
          field:
            pageName === "subscriptions" ? "n_subscription_orders" : "n_orders",
          headerComponent: HeaderComponent,
          headerComponentParams: {
            headerLabel:
              pageName === "subscriptions" ? "Subscription Orders" : "Orders",
            index: -1,
            pageName,
            experienceCategory: experience?.category,
            isPrimary: false,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return noDecimalNumberFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        {
          headerName: "Net Revenue",
          flex: 0.5,
          minWidth: 120,
          field: "net_revenue",
          headerComponent: HeaderComponent,
          headerComponentParams: {
            headerLabel: "Net Revenue",
            index: -1,
            pageName,
            experienceCategory: experience?.category,
            isPrimary: false,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return currencyFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        ...filteredGraphConfigs.map((config, index) => {
          const headerLabel = config.title[0];
          const metricToUse = config.impactMetric ?? config.metrics[0];
          const isPrimary =
            (STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryMetricId]
              ?.analyticsMetricId || primaryMetricId) === metricToUse;
          const isCustomMetric =
            getStandardMetricByAnalyticsMetricId(metricToUse) === undefined;

          return {
            headerName: headerLabel,
            headerComponent: HeaderComponent,
            headerComponentParams: {
              headerLabel,
              index,
              pageName,
              experienceCategory: experience?.category,
              isPrimary,
            },
            flex: experience?.category === "experiment" ? 0.7 : 0.4,
            minWidth: 150,
            field: config.impactMetric
              ? config.impactMetric
              : metricFilterIndex && config.metrics.length > 1
                ? config.metrics[metricFilterIndex]
                : config.metrics[0],
            cellDataType: false,
            cellRendererParams: {
              config,
              tableData: selectedData[0].data?.metrics || [],
              storeCurrencyConfig: storeCurrencyConfig!.value,
              testDuration,
              controlVariationId,
              variations,
              currValue,
              pageName,
              experienceId: experience?.id,
              isCustomMetric,
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value?.value !== undefined) {
                return (
                  graphValueFormatter({
                    isAxisTick: false,
                    value: params.value?.value,
                    configDataTypes: config.dataTypes,
                    decimalAmountShown: undefined,
                  }) || ""
                );
              } else return "";
            },
            cellStyle: { justifyContent: "flex-start" },
            cellRenderer: MetricCellRenderer,
          };
        }),
      ].filter((col) => !excludeColumns?.includes(col.field ?? ""));

      if (experience?.category === "experiment") {
        return tabValue === 0
          ? [
              ...baseColumns.slice(1, 2),
              {
                headerName: "Actions",
                field: "actions",
                flex: 0.6,
                headerComponent: HeaderComponent,
                headerComponentParams: {
                  headerLabel: "Actions",
                  index: -1,
                  pageName,
                  experienceCategory: experience?.category,
                  isPrimary: false,
                },
                cellRenderer: TableRolloutButtonRenderer,
                cellRendererParams: {
                  experience,
                  variations,
                  tabValue,
                  audienceSegmentSelected,
                  dispatch,
                  hasPersonalizationAccess,
                },
              },
              ...colDef,
            ]
          : [
              ...baseColumns,
              {
                headerName: "Actions",
                field: "actions",
                flex: 0.6,
                headerComponent: HeaderComponent,
                headerComponentParams: {
                  headerLabel: "Actions",
                  index: -1,
                  pageName,
                  experienceCategory: experience?.category,
                  isPrimary: false,
                },
                cellRenderer: TableRolloutButtonRenderer,
                cellRendererParams: {
                  experience,
                  variations,
                  tabValue,
                  audienceSegmentSelected,
                  dispatch,
                  hasPersonalizationAccess,
                },
              },
              ...colDef,
            ];
      } else {
        return tabValue === 0
          ? [...baseColumns.slice(1, 2), ...colDef]
          : [...baseColumns, ...colDef];
      }
    }, [
      tabValue,
      currValue,
      experience,
      selectedData[0].data,
      selectedData[0].isLoading,
      pageName,
      metricFilterIndex,
      shouldShowProfit,
      variations,
      controlVariationId,
      dashboardData,
      graphConfigs,
      storeCurrencyConfig,
      testDuration,
      primaryMetricId,
      graphValueFormatter,
    ]);

    const gridOptions: GridOptions = {
      rowSelection: {
        groupSelects: "descendants",
        mode: "multiRow",
        headerCheckbox: false,
        checkboxLocation: undefined,
        checkboxes: false,
      },
      context: {
        reportingCurrency: storeCurrencyConfig?.value?.code || "USD",
      },
      suppressCellFocus: true,
      preventDefaultOnContextMenu: true,
      suppressContextMenu: true,
      suppressClickEdit: true,
      domLayout: "normal",
      suppressDragLeaveHidesColumns: true,
      groupDisplayType: "groupRows",
      alwaysShowHorizontalScroll: true,
      alwaysShowVerticalScroll: true,
      ensureDomOrder: true,
      enableCellTextSelection: false,
      rowHeight: ROW_HEIGHT,
      headerHeight: HEADER_HEIGHT,
      groupDefaultExpanded: 1,
      cellSelection: {
        handle: {
          mode: "range",
        },
      },
      overlayLoadingTemplate: "<div>Loading Data... </div>",
      overlayNoRowsTemplate:
        "<div>No Data - Your filters may have resulted in no data, try resetting them</div>",
      tooltipShowDelay: 0,
    };

    return (
      <StyledGrid
        container
        direction={"column"}
        sx={{ height: `${gridHeight}px`, marginTop: "0px" }}
      >
        <AgGridReact
          ref={ref}
          theme={myTheme}
          defaultColDef={defaultColDef}
          rowData={
            isAudienceAnalyticsFetching > 0 || isOverviewFetching > 0
              ? []
              : dashboardData
                ? rows
                : []
          }
          columnDefs={columnDefs}
          loading={isAudienceAnalyticsFetching > 0 || isOverviewFetching > 0}
          groupRowRendererParams={groupRowRendererParams}
          gridOptions={gridOptions}
        />
      </StyledGrid>
    );
  }
);
